// EntityActionMenu — a compact "⋮" kebab that collapses an entity's ordinary
// actions (Redigera, Förhandsgranska, Duplicera, state toggles …) into one menu,
// so a detail header can read as "[Chatta med Link ●] [⋮]" instead of a spread of
// buttons (B6 follow-up). Pass a flat list of actions; falsy entries are skipped
// so callers can conditionally include state-dependent actions inline.
import React, { useEffect, useRef, useState } from 'react';
import { MoreVertical } from 'lucide-react';

export interface EntityAction {
    label: string;
    // Any icon-shaped component: a lucide icon OR a brand mark like LinkAvatar.
    icon?: React.ComponentType<{ className?: string }>;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    destructive?: boolean;
    // Small uppercase pill shown before the label (e.g. "dev" for test-only items).
    badge?: string;
    // Optional secondary line under the label explaining what the action does.
    description?: string;
}

export const EntityActionMenu: React.FC<{
    actions: (EntityAction | false | null | undefined)[];
    className?: string;
}> = ({ actions, className = '' }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const visible = actions.filter(Boolean) as EntityAction[];
    if (visible.length === 0) return null;

    return (
        <div className={`relative ${className}`} ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label="Fler åtgärder"
                title="Fler åtgärder"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900"
            >
                <MoreVertical className="h-4 w-4" />
            </button>
            {open && (
                <div className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 shadow-xl animate-in fade-in slide-in-from-top-1 duration-150">
                    {visible.map((a, i) => {
                        const Icon = a.icon;
                        return (
                            <button
                                key={i}
                                type="button"
                                disabled={a.disabled || a.loading}
                                onClick={() => { setOpen(false); a.onClick(); }}
                                className={`flex w-full items-start gap-2.5 px-3.5 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                                    a.destructive ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                {Icon && <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />}
                                <span className="flex-1 min-w-0">
                                    <span className="flex items-center gap-1.5">
                                        {a.badge && (
                                            <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                                {a.badge}
                                            </span>
                                        )}
                                        <span className="truncate">{a.label}</span>
                                    </span>
                                    {a.description && (
                                        <span className="mt-0.5 block text-xs font-normal text-gray-400">
                                            {a.description}
                                        </span>
                                    )}
                                </span>
                                {a.loading && <span className="text-xs text-gray-400">…</span>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default EntityActionMenu;
